import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input, Select } from '../components/form';
import { Alert, Button, Card, EmptyState, Spinner } from '../components/ui';
import { dataUtcBr, mesAno, tamanhoArquivo } from '../lib/format';
import styles from './screens.module.css';

interface Competencia {
  id: number;
  ano: number;
  mes: number;
}
interface Documento {
  id: number;
  tipo: string;
  competenciaId: number | null;
  nomeOriginal: string | null;
  tamanhoBytes: number | null;
  enviadoEm: string | null;
}

type Aviso = { kind: 'success' | 'danger'; texto: string };

/** Rótulo de cada tipo de documento (os de sistema só aparecem na lista). */
const TIPOS: Record<string, string> = {
  comprovante: 'Comprovante',
  titulo: 'Título',
  posicao_fundos: 'Posição de fundos',
  posicao_renda_fixa: 'Posição de renda fixa',
  outro: 'Outro',
  extrato_csv: 'Extrato (CSV)',
  extrato_pdf: 'Extrato (PDF)',
  foto_gas: 'Foto do medidor',
  demonstrativo_pdf: 'Demonstrativo (PDF)',
  balancete_pdf: 'Balancete (PDF)',
  balancete_docx: 'Balancete (Word)',
};
/** Tipos que o síndico escolhe ao enviar (os demais nascem de outras telas). */
const TIPOS_DE_ENVIO = ['comprovante', 'titulo', 'posicao_fundos', 'posicao_renda_fixa', 'outro'];
const MSG_ARQUIVO = 'Escolha o arquivo do documento.';
/** Erros da API de envio (código → mensagem). */
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  invalid_tipo: 'Tipo de documento inválido.',
  file_too_large: 'O arquivo passa de 25 MB.',
  missing_file: MSG_ARQUIVO,
  competencia_not_found: 'Competência não encontrada.',
  invalid_competencia: 'Escolha a competência do documento.',
};
const ERRO_GERAL_PADRAO = 'Não foi possível enviar. Tente novamente.';

/**
 * Documentos do condomínio (RF-A12): lista com download pelo proxy do Worker (o R2 é privado
 * e a chave nunca chega à tela) e envio com tipo e competência opcional. `editavel` só para o
 * síndico de condomínio ativo (POST é `requireRole('sindico')`); contábil e tenant suspenso
 * (#127) veem e baixam.
 */
export function Documentos({ editavel }: { editavel: boolean }) {
  const [comps, setComps] = useState<Competencia[]>([]);
  const [docs, setDocs] = useState<Documento[] | 'erro' | null>(null);
  const [aviso, setAviso] = useState<Aviso | null>(null);

  const carregar = useCallback(async () => {
    const dados = await getJson<{ documentos: Documento[] }>('/api/documentos');
    setDocs(dados ? dados.documentos : 'erro');
  }, []);

  useEffect(() => {
    void carregar();
    void getJson<{ competencias: Competencia[] }>('/api/competencias').then((d) =>
      setComps(d?.competencias ?? []),
    );
  }, [carregar]);

  async function aoConcluir(resultado: Aviso) {
    setAviso(resultado);
    if (resultado.kind === 'success') await carregar();
  }

  if (docs === null) return <Spinner />;
  const competencia = (id: number | null) => {
    const c = comps.find((x) => x.id === id);
    return c ? mesAno(c) : 'Sem competência';
  };

  return (
    <div className={styles.stack}>
      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}
      {editavel ? <EnvioDocumento comps={comps} onConcluido={aoConcluir} /> : null}

      {docs === 'erro' ? (
        <Alert kind="danger">Não foi possível carregar os documentos.</Alert>
      ) : docs.length === 0 ? (
        <EmptyState>Nenhum documento enviado ainda.</EmptyState>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Competência</th>
                <th className={styles.num}>Tamanho</th>
                <th>Enviado em</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const nome = d.nomeOriginal ?? `Documento #${d.id}`;
                return (
                  <tr key={d.id}>
                    <th scope="row">{nome}</th>
                    <td>{TIPOS[d.tipo] ?? d.tipo}</td>
                    <td>{competencia(d.competenciaId)}</td>
                    <td className={styles.num}>
                      {d.tamanhoBytes !== null ? tamanhoArquivo(d.tamanhoBytes) : '—'}
                    </td>
                    <td>{d.enviadoEm ? dataUtcBr(d.enviadoEm) : '—'}</td>
                    <td>
                      <a href={`/api/documentos/${d.id}/download`} aria-label={`Baixar ${nome}`}>
                        Baixar
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Envio de um documento: tipo, competência opcional e arquivo (multipart). */
function EnvioDocumento({
  comps,
  onConcluido,
}: {
  comps: Competencia[];
  onConcluido: (aviso: Aviso) => Promise<void>;
}) {
  const [tipo, setTipo] = useState('comprovante');
  const [competencia, setCompetencia] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  // Troca a cada envio concluído para esvaziar o campo de arquivo (não controlado).
  const [rodada, setRodada] = useState(0);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (enviando) return;
    if (!arquivo) {
      setErro(MSG_ARQUIVO);
      return;
    }
    setErro(null);
    const form = new FormData();
    form.set('file', arquivo);
    form.set('tipo', tipo);
    if (competencia) form.set('competenciaId', competencia);

    setEnviando(true);
    const res = await mutateJson<{ error?: string }>('/api/documentos', form);
    setEnviando(false);
    if (!res.ok) {
      const codigo = res.data?.error;
      await onConcluido({
        kind: 'danger',
        texto: (codigo && ERRO_GERAL[codigo]) || ERRO_GERAL_PADRAO,
      });
      return;
    }
    setArquivo(null);
    setRodada((r) => r + 1);
    await onConcluido({ kind: 'success', texto: 'Documento enviado.' });
  }

  return (
    <Card title="Enviar documento">
      <form onSubmit={enviar} noValidate>
        <div className={styles.row}>
          <Field label="Tipo">
            {(id) => (
              <Select id={id} value={tipo} onChange={(e) => setTipo(e.target.value)}>
                {TIPOS_DE_ENVIO.map((t) => (
                  <option key={t} value={t}>
                    {TIPOS[t]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Competência (opcional)">
            {(id) => (
              <Select
                id={id}
                value={competencia}
                onChange={(e) => setCompetencia(e.target.value)}
                style={{ maxWidth: '12rem' }}
              >
                <option value="">Sem competência</option>
                {comps.map((c) => (
                  <option key={c.id} value={c.id}>
                    {mesAno(c)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Arquivo" error={erro}>
            {(id) => (
              <Input
                key={rodada}
                id={id}
                type="file"
                onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
              />
            )}
          </Field>
        </div>
        <Button type="submit" disabled={enviando}>
          Enviar documento
        </Button>
      </form>
    </Card>
  );
}
